/**
 * Which document types a consumer request of each type can carry.
 *
 * THE VOCABULARY DOES NOT LIVE HERE, and that is deliberate. The authoritative
 * union is `NotarialRequestDocumentType` in the backend entity, where a
 * compile-time guard keeps professional-direction types out of the
 * consumer-direction list (decisions 7de89faf / 607feddc / 03f32d84). Moving
 * that here would drag the guard with it and weaken a separation four call
 * sites depend on. What moves is only the MAPPING — which subset each request
 * type may use — because that is what more than one app needs to agree on.
 *
 * Drift is caught at compile time from both ends rather than trusted: the
 * backend asserts every slug here is a real `NotarialRequestDocumentType`, and
 * the portal asserts the same against its own union. A typo in this file fails
 * both builds instead of silently offering a type nothing can store.
 *
 * Extracted from a hardcoded ternary in `user-request-detail-page.tsx`, which
 * was about to be copied a third time for the professional's required-document
 * picker (spec 18c82ff1 §5f). Behaviour is identical to that ternary — the
 * lists and the fallback are the same values in the same order.
 */
/** Documents a company-formation request accepts. */
const NOTARIAL_LEGAL_ENTITY_DOCUMENT_TYPES = [
    'estatuto',
    'comunicacion_bcu',
    'legal_document_front',
    'legal_document_back',
    'passport_document',
    'otra_documentacion_notarial',
];
/** Documents an income-certificate request accepts. */
const INCOME_CERTIFICATE_DOCUMENT_TYPES = [
    'income_special_format_template',
    'income_dgi_certificate',
    'income_dgi_form_6906',
    'income_bps_certificate',
    'income_tarjeta_rut',
    'income_professional_caja_certificate',
    'income_salary_receipt',
    'income_invoice',
    'otra_documentacion_notarial',
];
/**
 * Everything else, including `custom_quote` — the type an awarded RFQ spawns.
 *
 * A single untyped slot is not an oversight to tidy away: an RFQ exists
 * precisely because the need is not a catalog product, so there is no fixed
 * list of documents it implies. Spec 18c82ff1 is what gives those requests a
 * real list — declared by the professional who will do the work, rather than
 * guessed from the request type.
 */
const FALLBACK_DOCUMENT_TYPES = ['otra_documentacion_notarial'];
export const REQUEST_DOCUMENT_TYPES_BY_REQUEST_TYPE = {
    notarial_legal_entity: NOTARIAL_LEGAL_ENTITY_DOCUMENT_TYPES,
    income_certificate: INCOME_CERTIFICATE_DOCUMENT_TYPES,
};
/**
 * The document types a request of this type may carry.
 *
 * Takes a plain string rather than a union because every caller holds the
 * request type as data off the wire, and an unknown value is a normal case
 * here, not an error — new request types appear before this map learns about
 * them, and the fallback is the correct answer until it does.
 */
export function documentTypesForRequestType(requestType) {
    if (requestType == null)
        return FALLBACK_DOCUMENT_TYPES;
    return (REQUEST_DOCUMENT_TYPES_BY_REQUEST_TYPE[requestType] ??
        FALLBACK_DOCUMENT_TYPES);
}
